/**
 * AYARLAR › DEFTER KİLİDİ. Yalnızca gerçek (kilitli) depoda görünür.
 *
 *  - Şimdi kilitle (bekleyen yazım + yeniden yükleme),
 *  - otomatik kilit süresi 5/15/30/60 dk (D2; "hiç" yok),
 *  - parola değiştir (kap yeniden şifrelenmez; yalnızca parola sarmalı),
 *  - yeni kurtarma ifadesi (eskisi geçersiz olur) — gösterilir ve doğrulatılır,
 *  - bu cihazda başka bir defter aç (D6, 08.10): buluttaki başka bir defter
 *    bu cihazdakinin YERİNE (`DefterDegistir`),
 *  - depo bilgisi: şifreli yazım yolu (Ç0: Firefox/Safari'de hangisi
 *    kullanılıyor, yalnızca burada görülebilir).
 */

import { PerdeTanisi } from "./PerdeTanisi";
import {
  ACILISTA_GIZLE_ANAHTARI,
  PERDEDE_GIZLE_ANAHTARI,
  PERDE_SURELERI_SN,
  perdeSuresi,
  perdeSuresiYaz,
  secenekAcik,
  secenekYaz,
  type PerdeSuresiSn,
} from "../../lib/tutarGizleme";
import { M } from "../../dil";
import { useEffect, useState } from "react";
import { KILIT_SURELERI_DK, type KilitDurumu } from "@parafon/contracts/local-api";
import { useRepositoryContext } from "../../repo/RepositoryContext";
import { useMutation } from "../../repo/useAsync";
import { Button, Field, InlineError, Notice, Select, TextInput } from "../../ui/primitives";
import { useYedek } from "../../yedek/YedekSaglayici";
import { KASA_SUNUCU_PAROLA_EN_AZ } from "@parafon/contracts/central-api";
import { DefterDegistir } from "../BulutYedegiKarti";
import { IfadeGosterVeDogrula } from "./IfadeGosterVeDogrula";
import { IFADE_DOGRULANDI_AYARI } from "./KurulumEkrani";
import { KILIT_SURESI_AYARI, kilitSuresi, kilitle, yeniProfilIcinKilitle } from "./OtomatikKilit";
import { ParolaAlanlari, parolaSorunu, type ParolaDegeri } from "./ParolaAlanlari";
import { useSinirlar } from "../../kabiliyet/KabiliyetSaglayici";
import { KilitliKabiliyet } from "../../kabiliyet/KilitliKabiliyet";
import { bilerekYenile } from "../../lib/sayfaCikisi";

const YOL_ADI = {
  tasi: M.kilit.kart.yolTasi,
  "iki-yuva": M.kilit.kart.yolIkiYuva,
} as const;

export function KilitKarti({
  yenidenYukle = bilerekYenile,
}: {
  readonly yenidenYukle?: () => void;
}) {
  const { repo, revision, invalidate } = useRepositoryContext();
  const kasaSiniri = useSinirlar().kasa;
  const kilit = repo.kilit;
  const [durum, setDurum] = useState<KilitDurumu | null>(null);
  const [sure, setSure] = useState<number | null>(null);
  const [parolaAcik, setParolaAcik] = useState(false);
  const [yeni, setYeni] = useState<ParolaDegeri>({ parola: "", tekrar: "" });
  const [sorun, setSorun] = useState<string | null>(null);
  const [tamam, setTamam] = useState<string | null>(null);
  const [kelimeler, setKelimeler] = useState<readonly string[] | null>(null);
  const [degistirAcik, setDegistirAcik] = useState(false);
  const [yeniEtiket, setYeniEtiket] = useState<string | null>(null);
  const [silSor, setSilSor] = useState(false);
  const [silOnay, setSilOnay] = useState("");
  const yedek = useYedek();
  const run = useMutation();

  useEffect(() => {
    if (kilit === undefined) return;
    void kilit.durum().then(setDurum, () => undefined);
    void repo.settings.get(KILIT_SURESI_AYARI).then((v) => setSure(kilitSuresi(v)), () => undefined);
  }, [repo, kilit, revision]);

  if (kilit === undefined) return null;

  const onSure = (v: string): void => {
    void run.run(async () => {
      await repo.settings.set(KILIT_SURESI_AYARI, v);
      setSure(kilitSuresi(v));
      invalidate();
    });
  };

  const onParola = (): void => {
    // Parafon sunucusu hedefinde 12 karakter ŞART (kullanıcı kararı 17.10.2026);
    // diğer hedeflerde kısa parola yalnızca uyarı.
    const s =
      parolaSorunu(yeni) ??
      (yedek?.durum.hedefTuru === "parafon-sunucu" && [...yeni.parola].length < KASA_SUNUCU_PAROLA_EN_AZ
        ? M.kilit.kart.sunucuParolaKisa(KASA_SUNUCU_PAROLA_EN_AZ)
        : null);
    setSorun(s);
    if (s !== null) return;
    void run.run(async () => {
      await kilit.parolaDegistir(yeni.parola);
      setYeni({ parola: "", tekrar: "" });
      setParolaAcik(false);
      setTamam(M.kilit.kart.parolaDegisti);
    });
  };

  const onIfade = (): void => {
    void run.run(async () => {
      setTamam(null);
      setKelimeler((await kilit.ifadeYenile()).kelimeler);
    });
  };

  return (
    <section className="pf-card space-y-4 p-4" data-kart-bolum="guvenlik" aria-labelledby="kilit-karti-baslik">
      <h2 id="kilit-karti-baslik" className="text-[14px] font-semibold text-ink">
        {M.kilit.kart.baslik}
      </h2>
      <p className="text-[12px] text-muted">{M.kilit.kart.aciklama}</p>

      <div className="flex flex-wrap items-end gap-3">
        <Button variant="primary" onClick={() => void kilitle(kilit, yenidenYukle)}>
          {M.kilit.kart.simdiKilitle}
        </Button>
        <Field label={M.kilit.kart.kendiligindenKilitle} htmlFor="kilit-sure">
          <Select
            id="kilit-sure"
            value={sure === null ? "" : String(sure)}
            onChange={(e) => onSure(e.currentTarget.value)}
            options={KILIT_SURELERI_DK.map((d) => ({ value: String(d), label: M.kilit.kart.dakikaSonra(d) }))}
          />
        </Field>
      </div>
      <GizlemeAyarlari />

      {!parolaAcik ? (
        <Button onClick={() => setParolaAcik(true)}>{M.kilit.kart.parolayiDegistir}</Button>
      ) : (
        <div className="space-y-3">
          <ParolaAlanlari deger={yeni} onDegis={setYeni} etiket={M.kilit.yeniParola} kimlik="kilit-degistir" />
          {sorun !== null && <InlineError error={new Error(sorun)} />}
          <div className="flex gap-2">
            <Button variant="primary" disabled={run.pending} onClick={onParola}>
              {M.kilit.kart.parolayiDegistir}
            </Button>
            <Button onClick={() => setParolaAcik(false)}>{M.ortak.vazgec}</Button>
          </div>
        </div>
      )}

      {kelimeler === null ? (
        <div className="space-y-1">
          <Button disabled={run.pending} onClick={onIfade}>
            {M.kilit.kart.yeniIfadeOlustur}
          </Button>
          <p className="text-[12px] text-muted">{M.kilit.kart.yeniIfadeAciklama}</p>
        </div>
      ) : (
        <IfadeGosterVeDogrula
          kelimeler={kelimeler}
          onDogrulandi={() =>
            void run.run(async () => {
              await repo.settings.set(IFADE_DOGRULANDI_AYARI, "1");
              setKelimeler(null);
              setTamam(M.kilit.kart.yeniIfadeDogrulandi);
              invalidate();
            })
          }
        />
      )}

      {tamam !== null && (
        <Notice tone="info" title={M.kilit.kart.tamam}>
          <p>{tamam}</p>
        </Notice>
      )}
      {run.error !== undefined && <InlineError error={run.error} />}

      {durum?.profil != null && (
        <div className="space-y-2 border-t border-line pt-3">
          <h3 className="text-[13px] font-semibold text-ink">{M.kilit.profil}</h3>
          {yeniEtiket === null ? (
            <p className="text-[13px] text-ink">
              {M.kilit.kart.acikProfil} <strong>{durum.profil.etiket}</strong>
              {durum.profiller.length > 1 && (
                <span className="text-muted">{M.kilit.kart.profilSayisi(durum.profiller.length)}</span>
              )}
            </p>
          ) : (
            <div className="flex flex-wrap items-end gap-2">
              <Field label={M.kilit.profilAdi} htmlFor="profil-etiket">
                <TextInput id="profil-etiket" maxLength={40} value={yeniEtiket} onChange={(e) => setYeniEtiket(e.currentTarget.value)} />
              </Field>
              <Button
                variant="primary"
                disabled={run.pending || yeniEtiket.trim().length === 0}
                onClick={() =>
                  void run.run(async () => {
                    await kilit.etiketDegistir(yeniEtiket.trim());
                    setYeniEtiket(null);
                    setDurum(await kilit.durum());
                  })
                }
              >
                {M.ortak.kaydet}
              </Button>
              <Button onClick={() => setYeniEtiket(null)}>{M.ortak.vazgec}</Button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {yeniEtiket === null && <Button onClick={() => setYeniEtiket(durum.profil?.etiket ?? "")}>{M.kilit.kart.adiniDegistir}</Button>}
            {durum.profiller.length > 1 && (
              <Button onClick={() => void kilitle(kilit, yenidenYukle)}>{M.kilit.kart.profilDegistir}</Button>
            )}
            {/* Gelir modeli (kullanıcı kararı 26.10.2026): yalnızca hakkı olan kasa açıkken. */}
            {(kasaSiniri === null || durum.profiller.length < kasaSiniri) && (
              <Button onClick={() => void yeniProfilIcinKilitle(kilit, yenidenYukle)}>{M.kilit.yeniProfilEkle}</Button>
            )}
            {!silSor && (
              <Button variant="danger" onClick={() => setSilSor(true)}>
                {M.kilit.kart.profiliSilSor}
              </Button>
            )}
          </div>
          {kasaSiniri !== null && durum.profiller.length >= kasaSiniri && (
            <KilitliKabiliyet ne={M.kilit.kart.yeniProfilKilitli} />
          )}
          {silSor && (
            <Notice tone="danger" title={M.kilit.kart.silBaslik}>
              <p>{M.kilit.kart.silMetin(durum.profil.etiket)}</p>
              <Field label={M.kilit.kart.silOnay(durum.profil.etiket)} htmlFor="profil-sil-onay">
                <TextInput id="profil-sil-onay" value={silOnay} onChange={(e) => setSilOnay(e.currentTarget.value)} />
              </Field>
              <div className="mt-2 flex gap-2">
                <Button
                  variant="danger"
                  disabled={run.pending || silOnay !== durum.profil.etiket}
                  onClick={() =>
                    void run.run(async () => {
                      await kilit.profilSil();
                      yenidenYukle();
                    })
                  }
                >
                  {M.kilit.kart.profiliSil}
                </Button>
                <Button
                  onClick={() => {
                    setSilSor(false);
                    setSilOnay("");
                  }}
                >
                  {M.ortak.vazgec}
                </Button>
              </div>
            </Notice>
          )}
        </div>
      )}

      {yedek?.kilitli === true &&
        (degistirAcik ? (
          <DefterDegistir onVazgec={() => setDegistirAcik(false)} yenidenYukle={yenidenYukle} />
        ) : (
          <div className="space-y-1">
            <Button onClick={() => setDegistirAcik(true)}>{M.kilit.kart.baskaKasaAc}</Button>
            <p className="text-[12px] text-muted">{M.kilit.kart.baskaKasaAciklama}</p>
          </div>
        ))}

      {durum?.yazimYolu != null && (
        <p className="text-[12px] text-faint">{M.kilit.kart.sifreliYazim(YOL_ADI[durum.yazimYolu])}</p>
      )}
      <PerdeTanisi />
    </section>
  );
}

/** Hareketsizlikte perde ve tutar gizleme seçenekleri (02.11.2026): yalnızca bu cihazda. */
function GizlemeAyarlari() {
  const [sn, setSn] = useState<PerdeSuresiSn>(perdeSuresi);
  const [acilista, setAcilista] = useState(() => secenekAcik(ACILISTA_GIZLE_ANAHTARI));
  const [perdede, setPerdede] = useState(() => secenekAcik(PERDEDE_GIZLE_ANAHTARI));
  const kutular = [
    { id: "gizle-acilista", acik: acilista, etiket: M.kilit.gizleme.acilistaGizle, anahtar: ACILISTA_GIZLE_ANAHTARI, set: setAcilista },
    { id: "gizle-perdede", acik: perdede, etiket: M.kilit.gizleme.perdedeGizle, anahtar: PERDEDE_GIZLE_ANAHTARI, set: setPerdede },
  ] as const;
  return (
    <div className="space-y-2">
      <Field label={M.kilit.gizleme.perdeSuresi} htmlFor="perde-sure" hint={M.kilit.gizleme.perdeAciklama}>
        <Select
          id="perde-sure"
          value={String(sn)}
          onChange={(e) => {
            const v = Number(e.currentTarget.value) as PerdeSuresiSn;
            perdeSuresiYaz(v);
            setSn(v);
          }}
          options={PERDE_SURELERI_SN.map((d) => ({ value: String(d), label: d === 0 ? M.kilit.gizleme.kapali : M.kilit.gizleme.saniye(d) }))}
        />
      </Field>
      {kutular.map((k) => (
        <label key={k.id} htmlFor={k.id} className="flex items-center gap-2 text-[13px] text-ink">
          <input
            id={k.id}
            type="checkbox"
            checked={k.acik}
            onChange={(e) => {
              secenekYaz(k.anahtar, e.currentTarget.checked);
              k.set(e.currentTarget.checked);
            }}
          />
          {k.etiket}
        </label>
      ))}
    </div>
  );
}
